import { createTheme } from "@material-ui/core/styles";
import { FONTS } from "../shared/utils/Fonts";

const FONTWEIGHT = {
  light: 300,
  regular: 400,
  bold: 700
};

const BREAKPOINTSVALUES = {
  xs: 0,
  sm: 600,
  md: 900,
  lg: 1200,
  xl: 1800
};

const SPACING = 8;

const marginAndPaddingClasses = {
  ".m-{i}": "margin",
  ".mb-{i}": "marginBottom",
  ".mt-{i}": "marginTop",
  ".mr-{i}": "marginRight",
  ".ml-{i}": "marginLeft",
  ".p-{i}": "padding",
  ".pb-{i}": "paddingBottom",
  ".pt-{i}": "paddingTop",
  ".pr-{i}": "paddingRight",
  ".pl-{i}": "paddingLeft"
};

export const FONTS_SIZE = {
  sizes: {
    size1: 0.625,
    size2: 0.75,
    size3: 0.875,
    size4: 1,
    size5: 1.125,
    size6: 1.3125,
    size7: 1.25,
    size8: 2.25
  },
  medida: "rem"
};

export const getLineHeight = size => size * 1.4 + FONTS_SIZE.medida;

const generateNClasses = (name, number, calc) => {
  let classes = {};

  for (let i = 0; i <= number; i++) {
    let className = name.replace("{i}", i);
    classes[className] = calc(i);
  }
  return classes;
};

const spacing = num => (num ? num * SPACING + "px" : "0");

const generateMarginAndPaddingClasses = prefix => {
  prefix = prefix || "";
  let classes = {};

  for (let cls in marginAndPaddingClasses) {
    let clsProp = marginAndPaddingClasses[cls];
    let tempClasses = generateNClasses(prefix + cls, 10, i => ({
      [clsProp]: spacing(i * 0.5)
    }));
    classes = {
      ...classes,
      ...tempClasses
    };
  }
  return classes;
};

const criarTema = PALETTE => {
  return createTheme({
    palette: PALETTE,
    spacing: SPACING,
    breakpoints: {
      values: BREAKPOINTSVALUES
    },
    overrides: {
      MuiOutlinedInput: {
        root: {
          "&.Mui-disabled": {
            backgroundColor: "rgba(192,192,222,0.15)"
          },
          "&.Mui-focused $notchedOutline": {
            borderColor: PALETTE.primary.main
          },
          color: PALETTE.secondary.main,
          fontWeight: "500",
          fontSize: "1rem"
        }
      },
      MuiFormLabel: {
        root: {
          fontFamily: FONTS.FONT_FAMILY_SEMI_BOLD,
          color: "#636D76",
          fontSize: "1rem",
          fontWeight: "500",
          "&.Mui-focused": {
            color: PALETTE.primary.main
          }
        },
        asterisk: {
          fontFamily: FONTS.FONT_FAMILY_BOLD,
          fontWeight: "600",
          marginTop: "-8px",
          fontSize: 30,
          color: PALETTE.primary.main,
          "&$error": {
            color: PALETTE.primary.main
          }
        }
      },
      MuiInputLabel: {
        root: {
          display: "flex"
        }
      },
      MuiButton: {
        root: {
          textTransform: "none",
          fontWeight: "600",
          "&.Mui-disabled": {
            backgroundColor: PALETTE.neutral.main,
            color: "#ffffff"
          },
          fontFamily: FONTS.FONT_FAMILY_SEMI_BOLD
        },
        contained: {
          backgroundColor: PALETTE.primary.main,
          "&:hover": {
            backgroundColor: PALETTE.primary.dark
          },
          color: "#ffffff"
        },
        containedSecondary: {
          backgroundColor: PALETTE.secondary.main,
          "&:hover": {
            backgroundColor: PALETTE.secondary.dark
          },
          color: "#ffffff"
        },
        text: {
          "&.Mui-disabled": {
            backgroundColor: "rgb(255, 255, 255, 00)",
            color: PALETTE.neutral.main
          }
        }
      },
      MuiTableCell: {
        root: {
          "&.spacing-1": {
            padding: "14px 8px 14px 8px"
          }
        },
        sizeSmall: {
          padding: `6px ${spacing(1)} 6px ${spacing(1)}`
        },
        head: {
          fontSize: "14px",
          fontFamily: FONTS.FONT_FAMILY_REGULAR,
          color: PALETTE.secondary.main,
          fontWeight: "normal",
          padding: `10px ${spacing(1)} 2px ${spacing(1)}`
        },
        footer: {
          borderBottom: "0px"
        },
        body: {
          fontSize: "16px",
          fontWeight: "500",
          color: PALETTE.secondary.main,
          padding: `10px ${spacing(1)} 10px ${spacing(1)}`,
          borderBottom: "1px solid #F9F9F9"
        }
      },
      MuiTablePagination: {
        toolbar: {
          paddingLeft: "0px",
          float: "left"
        },
        caption: {
          fontSize: "14px",
          color: PALETTE.secondary.main
        }
      },
      MuiFormHelperText: {
        contained: {
          marginLeft: "0px"
        }
      },
      MuiCheckbox: {
        root: {
          "&.Mui-disabled.Mui-checked": {
            color: PALETTE.neutral.main
          },
          "&.Mui-disabled": {
            color: `${PALETTE.neutral.main} !important`
          }
        },
        colorSecondary: {
          "&.Mui-disabled.Mui-checked": {
            color: PALETTE.neutral.main
          },
          "&.Mui-disabled": {
            color: PALETTE.neutral.main
          }
        }
      },
      MuiRadio: {
        root: {
          "&.Mui-disabled": {
            color: PALETTE.neutral.main
          }
        },
        colorSecondary: {
          "&.Mui-disabled": {
            color: PALETTE.secondary.main
          }
        }
      },
      MuiTab: {
        root: {
          "&.Mui-selected": {
            color: PALETTE.primary.main
          },
          fontWeight: "600",
          fontSize: "1rem",
          fontFamily: FONTS.FONT_FAMILY_SEMI_BOLD,
          "&.MuiTab-textColorPrimary.Mui-disabled": {
            color: PALETTE.neutral.main
          }
        },
        "&$disabled": {
          color: PALETTE.neutral.main
        }
      },
      MuiButtonBase: {
        root: {
          textTransform: "none !important",
          minWidth: "0px !important"
        }
      },
      MuiPickersDay: {
        current: {
          border: `1px solid ${PALETTE.primary.main}`
        },
        daySelected: {
          backgroundColor: PALETTE.primary.main,
          "&:hover": {
            backgroundColor: PALETTE.primary.main
          }
        },
        day: {
          "& > span > p": {
            fontSize: "14px",
            fontWeight: "500"
          }
        }
      },
      MuiPickersCalendar: {
        transitionContainer: {
          minHeight: "190px"
        }
      },
      MuiPickersBasePicker: {
        pickerViewLandscape: {
          minHeight: "267px"
        }
      },
      MuiPickersCalendarHeader: {
        dayLabel: {
          fontSize: "12px",
          color: PALETTE.neutral.main,
          fontWeight: "500"
        },
        switchHeader: {
          width: "55%",
          marginTop: "0px",
          marginBottom: "0px"
        }
      },
      MuiAccordion: {
        root: {
          border: "none",
          borderBottom: `1px solid #E3E8F1`,
          boxShadow: "none",
          "&.Mui-expanded": {
            margin: "0px"
          },
          "&:before": {
            display: "none"
          }
        }
      },
      MuiAccordionSummary: {
        root: {
          padding: "0 24px",
          margin: "0px",
          minHeight: "50px",
          "&$expanded": {
            minHeight: "40px"
          },
          "&:before": {
            display: "none"
          }
        },
        content: {
          margin: "0px",
          "&$expanded": {
            margin: "0px"
          }
        },
        expandIcon: {
          paddingTop: "0px",
          paddingBottom: "0px"
        }
      },
      MuiAccordionDetails: {
        root: {
          padding: "0px"
        }
      },
      MuiCollapse: {
        wrapper: {
          paddingBottom: "10px"
        }
      },
      MuiSwitch: {
        track: {
          backgroundColor: PALETTE.primary.main,
          opacity: "0.5"
        },
        thumb: {
          color: PALETTE.primary.main
        }
      },
      MuiGrid: {
        root: {
          ...generateMarginAndPaddingClasses("&.MuiGrid-item")
        }
      },
      MuiDivider: {
        root: {
          ...generateMarginAndPaddingClasses("&")
        }
      }
    }
  });
};

const criarTypography = (PALETTE, themeModel) => {
  return {
    fontFamily: FONTS.FONT_FAMILY_SEMI_BOLD,
    fontWeightLight: FONTWEIGHT.light,
    fontWeightRegular: FONTWEIGHT.regular,
    fontWeightBold: FONTWEIGHT.bold,
    h1: {
      fontSize: FONTS_SIZE.sizes.size8 + FONTS_SIZE.medida,
      color: PALETTE.secondary.main,
      fontFamily: FONTS.FONT_FAMILY_BOLD,
      lineHeight: getLineHeight(FONTS_SIZE.sizes.size8),
      [themeModel.breakpoints.down("xs")]: {
        fontSize: FONTS_SIZE.sizes.size7 + FONTS_SIZE.medida,
        lineHeight: getLineHeight(FONTS_SIZE.sizes.size7)
      }
    },
    h2: {
      fontSize: FONTS_SIZE.sizes.size7 + FONTS_SIZE.medida,
      color: PALETTE.secondary.main,
      fontFamily: FONTS.FONT_FAMILY_BOLD,
      lineHeight: getLineHeight(FONTS_SIZE.sizes.size7),
      [themeModel.breakpoints.down("xs")]: {
        fontSize: FONTS_SIZE.sizes.size6 + FONTS_SIZE.medida,
        lineHeight: getLineHeight(FONTS_SIZE.sizes.size6)
      }
    },
    h3: {
      fontSize: FONTS_SIZE.sizes.size6 + FONTS_SIZE.medida,
      color: PALETTE.secondary.main,
      fontFamily: FONTS.FONT_FAMILY_BOLD,
      lineHeight: getLineHeight(FONTS_SIZE.sizes.size6),
      [themeModel.breakpoints.down("xs")]: {
        fontSize: FONTS_SIZE.sizes.size5 + FONTS_SIZE.medida,
        lineHeight: getLineHeight(FONTS_SIZE.sizes.size5)
      }
    },
    h4: {
      fontSize: FONTS_SIZE.sizes.size5 + FONTS_SIZE.medida,
      color: PALETTE.secondary.main,
      fontWeight: FONTS.FONT_FAMILY_SEMI_BOLD,
      fontFamily: FONTS.FONT_FAMILY_SEMI_BOLD,
      lineHeight: getLineHeight(FONTS_SIZE.sizes.size5),
      [themeModel.breakpoints.down("xs")]: {
        fontSize: FONTS_SIZE.sizes.size4 + FONTS_SIZE.medida,
        lineHeight: getLineHeight(FONTS_SIZE.sizes.size4)
      }
    },
    h5: {
      fontSize: FONTS_SIZE.sizes.size4 + FONTS_SIZE.medida,
      color: PALETTE.secondary.main,
      fontWeight: FONTS.FONT_FAMILY_SEMI_BOLD,
      fontFamily: FONTS.FONT_FAMILY_SEMI_BOLD,
      lineHeight: getLineHeight(FONTS_SIZE.sizes.size4),
      [themeModel.breakpoints.down("xs")]: {
        fontSize: FONTS_SIZE.sizes.size3 + FONTS_SIZE.medida,
        lineHeight: getLineHeight(FONTS_SIZE.sizes.size3)
      }
    },
    h6: {
      fontSize: FONTS_SIZE.sizes.size3 + FONTS_SIZE.medida,
      color: PALETTE.secondary.main,
      fontWeight: FONTS.FONT_FAMILY_SEMI_BOLD,
      fontFamily: FONTS.FONT_FAMILY_SEMI_BOLD,
      lineHeight: getLineHeight(FONTS_SIZE.sizes.size3),
      [themeModel.breakpoints.down("xs")]: {
        fontSize: FONTS_SIZE.sizes.size2 + FONTS_SIZE.medida,
        lineHeight: getLineHeight(FONTS_SIZE.sizes.size2)
      }
    },
    body1: {
      fontSize: FONTS_SIZE.sizes.size2 + FONTS_SIZE.medida,
      color: PALETTE.secondary.main,
      fontWeight: FONTS.FONT_FAMILY_SEMI_BOLD,
      fontFamily: FONTS.FONT_FAMILY_SEMI_BOLD,
      lineHeight: getLineHeight(FONTS_SIZE.sizes.size2)
    },
    body2: {
      fontSize: FONTS_SIZE.sizes.size1 + FONTS_SIZE.medida,
      fontFamily: FONTS.FONT_FAMILY_SEMI_BOLD,
      lineHeight: getLineHeight(FONTS_SIZE.sizes.size1)
    },
    subtitle1: {
      fontSize: FONTS_SIZE.sizes.size4 + FONTS_SIZE.medida,
      color: PALETTE.neutral.main,
      fontWeight: FONTS.FONT_FAMILY_SEMI_BOLD,
      fontFamily: FONTS.FONT_FAMILY_SEMI_BOLD,
      lineHeight: getLineHeight(FONTS_SIZE.sizes.size4),
      whiteSpace: "pre-line"
    },
    subtitle2: {
      fontSize: FONTS_SIZE.sizes.size2 + FONTS_SIZE.medida,
      color: PALETTE.neutral.main,
      fontWeight: FONTS.FONT_FAMILY_REGULAR,
      fontFamily: FONTS.FONT_FAMILY_REGULAR,
      lineHeight: getLineHeight(FONTS_SIZE.sizes.size2)
    },
    tableCell: {
      fontSize: FONTS_SIZE.sizes.size3 + FONTS_SIZE.medida,
      color: PALETTE.secondary.main,
      fontFamily: FONTS.FONT_FAMILY_REGULAR,
      lineHeight: getLineHeight(FONTS_SIZE.sizes.size3)
    }
  };
};

const customizarTemaGlobal = PALETTE => {
  return {
    ...generateMarginAndPaddingClasses(),
    "*::-webkit-scrollbar": {
      width: "8px",
      height: "8px"
    },
    "*::-webkit-scrollbar-track": {
      background: "#f1f1f1"
    },
    "*::-webkit-scrollbar-thumb": {
      background: PALETTE.neutral.main,
      borderRadius: "10px"
    },
    "*::-webkit-scrollbar-thumb:hover": {
      background: PALETTE.secondary.main
    },
    "::-webkit-scrollbar-track-piece": {
      backgroundColor: "#ffffff"
    },
    ".text-select-disabled": {
      "-webkit-touch-callout": "none" /* iOS Safari */,
      "-webkit-user-select": "none" /* Safari */,
      "-moz-user-select": "none" /* Firefox */,
      "-ms-user-select": "none" /* Internet Explorer/Edge */,
      "user-select": "none"
    },
    ".subtitle3": {
      fontSize: "1.2rem",
      color: PALETTE.neutral.main,
      lineHeight: 1.2,
      fontFamily: FONTS.FONT_FAMILY_LIGHT
    },
    ".subtitle4": {
      fontSize: "0.75rem",
      color: PALETTE.secondary.main,
      lineHeight: 1.2,
      fontFamily: FONTS.FONT_FAMILY_SEMI_BOLD
    },
    ".subtitle5": {
      fontSize: FONTS_SIZE.sizes.size5 + FONTS_SIZE.medida,
      color: PALETTE.secondary.main,
      fontWeight: FONTS.FONT_FAMILY_SEMI_BOLD,
      fontFamily: FONTS.FONT_FAMILY_SEMI_BOLD,
      lineHeight: getLineHeight(FONTS_SIZE.sizes.size4)
    },
    ".text-overflow-ellipsis": {
      textOverflow: "ellipsis",
      overflow: "hidden",
      whiteSpace: "nowrap"
    },
    ".italic": {
      fontStyle: "italic"
    },
    ".bold": {
      fontWeight: "bold"
    },
    ".semi-bold": {
      fontWeight: "600"
    },
    ".fonte-media": {
      fontFamily: FONTS.FONT_FAMILY_MEDIUM,
      fontWeight: 400
    },
    ".required": {
      "&::after": {
        content: "'*'",
        fontWeight: "600",
        marginTop: "-6px",
        fontSize: 30,
        color: PALETTE.primary.main
      }
    },
    ".bolaVermelha": {
      borderRadius: "50%",
      height: "5px",
      width: "5px",
      backgroundColor: PALETTE.primary.main,
      display: "inline-block",
      marginBottom: "4px",
      marginRight: "12px"
    },
    ".body3": {
      fontWeight: "lighter",
      lineHeight: "1.4"
    },
    ".highlight": {
      backgroundColor: PALETTE.primary.light
    },
    ".highlight-blue *": {
      backgroundColor: `rgb(232, 244, 253) !important`
    },
    ".fonteCorNeutra": {
      color: PALETTE.neutral.dark
    },
    ".linkButtonDesabilitado": {
      color: PALETTE.neutral.main
    },
    ".linkButton": {
      color: PALETTE.primary.main
    },
    ".rodape": {
      backgroundColor: PALETTE.secondary.main,
      marginTop: "5rem",
      padding: "1.25rem",
      paddingLeft: "3.75rem",
      paddingRight: "3.75rem",
      "@media(max-width: 480px)": {
        paddingLeft: "2.5rem",
        paddingRight: "2.5rem"
      }
    },
    ".container-atributo img": {
      maxWidth: "100%",
      maxHeight: "100%"
    },
    ".container-atributo": {
      marginTop: "12px",
      marginBottom: "32px",
      whiteSpace: "pre-line"
    },
    ".marginP .ck-content p": {
      marginTop: "12px"
    },
    ".ck-content pre": {
      padding: "0 0 1rem 0",
      backgroundColor: PALETTE.background.main
    },
    ".compartilhar": {
      color: PALETTE.primary.main
    },
    ".tituloH2PesquisaMultipla": {
      color: PALETTE.primary.main
    },
    ".textoAtencaoVisualizacaoMultipla": {
      color: PALETTE.primary.main,
      width: "100%",
      fontFamily: FONTS.FONT_FAMILY_BOLD
    },
    ".indicator": {
      left: "0px",
      backgroundColor: PALETTE.primary.main
    },
    ".skeleton": {
      backgroundColor: "#E3E8F1"
    },
    ".MuiTooltip-popper": {
      zIndex: 100000
    },
    ".scrollMarginTop": { scrollMarginTop: "4.5rem" }
  };
};

export const defaultTheme = PALETTE => {
  const themeModel = criarTema(PALETTE);

  const typography = criarTypography(PALETTE, themeModel);

  themeModel.typography = Object.assign({}, themeModel.typography, typography);

  themeModel.global = customizarTemaGlobal(PALETTE);

  return themeModel;
};
